<script setup lang="ts">
import { ref, onMounted, toRefs, watch } from 'vue';
import EditGradesModal from '@/components/Grades/EditGradesModal.vue';
import { PERMISSIONS, GRADES_ORDER } from '@/enum';
import { can } from '@/composables';
import { PersonGrades } from '@/types/GradeInterface';
import { useGradeStore } from '@/store';

type FormattedGradesData = {
  organization_id: number;
  organization_name: string;
  grades: PersonGrades[];
};

type OrganizationInterface = {
  id: number;
  name: string;
  parent_id: number | null;
};

const props = defineProps<{ flattenOrganizations: OrganizationInterface[]; userId: number }>();
const gradesStore = useGradeStore();
const formatedGrades = ref<FormattedGradesData[]>([]);
const isModalOpen = ref(false);
const currentGrades = ref<PersonGrades[]>([]);
const currentOrganizationId = ref(0);
const currentOrganizationName = ref<string | null>(null);
const { flattenOrganizations, userId } = toRefs(props);

const init = async (): Promise<void> => {
  isModalOpen.value = false;
  gradesStore.personGrades = [];
  await gradesStore.fetchPersonGrades(userId.value);
  const allGrades = gradesStore.personGrades;
  const childOrgs = flattenOrganizations.value.filter(
    (obj, index, self) => index === self.findIndex((t) => t.id === obj.id),
  );

  const grouped = childOrgs.map((org) => {
    const orgGrades = allGrades.filter((grade) => grade.organization_id === org.id);
    if (orgGrades.length > 0) {
      orgGrades.sort((a, b) => {
        const indexA = GRADES_ORDER.findIndex((g) => g === a.grade);
        const indexB = GRADES_ORDER.findIndex((g) => g === b.grade);
        const safeIndexA = indexA === -1 ? GRADES_ORDER.length : indexA;
        const safeIndexB = indexB === -1 ? GRADES_ORDER.length : indexB;
        return safeIndexA - safeIndexB;
      });
    }
    return {
      organization_id: org.id,
      organization_name: org.name,
      grades: orgGrades,
    };
  });

  formatedGrades.value = grouped;
};

onMounted(() => {
  init();
});

watch(flattenOrganizations, () => {
  init();
});

watch(userId, () => {
  init();
});

const onEdit = (data: FormattedGradesData) => {
  currentGrades.value = data.grades;
  currentOrganizationId.value = data.organization_id;
  currentOrganizationName.value = data.organization_name;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2 class="text-xl font-semibold">Grades</h2>
    </template>
    <template #content>
      <DataTable
        :value="formatedGrades"
        :loading="gradesStore.loading"
        data-key="organization_id"
        responsive-layout="scroll"
        :sort-field="'organization_name'"
        :sort-order="1"
      >
        <template v-if="formatedGrades.length > 0">
          <Column field="organization_name" header="Organizations" sortable />
          <Column header="Grades">
            <template #body="{ data }">
              <span v-if="data.grades.length > 0">
                {{ data.grades.map((g) => g.grade).join(', ') }}
              </span>
              <span v-else>All grades</span>
            </template>
          </Column>
          <Column v-if="can([PERMISSIONS.GRADE_ASSIGN])" field="actions" header="Actions">
            <template #body="{ data }">
              <a class="flex items-center space-x-2" @click="onEdit(data)">
                <Icon name="edit" class="text-lg text-blue-500" />
                <span class="text-sm text-gray-800">Edit</span>
              </a>
            </template>
          </Column>
        </template>
        <template #empty>All Grades.</template>
      </DataTable>
    </template>
  </Card>
  <EditGradesModal
    v-if="can([PERMISSIONS.GRADE_ASSIGN]) && currentGrades"
    :visible="isModalOpen"
    :person-id="props.userId"
    :organization-id="currentOrganizationId"
    :organization-name="currentOrganizationName"
    :current-grades="currentGrades"
    @cancel="closeModal"
    @sync="init"
  />
</template>
